import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { initRUM } from "./lib/rum";
import { initClarity } from "./lib/clarity";
import { initOfflineQueue } from "./lib/offlineQueue";
import { registerAllQueueHandlers } from "./lib/offlineQueueHandlers";
import { registerAppShellSW } from "./lib/registerAppShellSW";

// Real User Monitoring — admin-only telemetry, never affects UX
initRUM();

// Offline action queue: register replay handlers, then start the auto-flusher.
// Cheap to boot; the queue stays empty until something actually fails.
registerAllQueueHandlers();
initOfflineQueue();

// App-shell service worker. The wrapper hard-refuses in dev/preview/iframe.
void registerAppShellSW();

// === CLARITY INIT === Microsoft Clarity (prod-only, public pages, skips /admin)
// Deferred to idle so it doesn't compete with LCP. Same pattern as gtag in index.html.
if (typeof window !== "undefined") {
  const ric = (window as any).requestIdleCallback as undefined | ((cb: () => void, opts?: { timeout: number }) => void);
  if (ric) ric(() => initClarity(), { timeout: 3000 });
  else setTimeout(() => initClarity(), 2500);
}

/**
 * Global Comic Button tilt director.
 * Sets --btn-comic-tilt on any .btn-comic the cursor/touch is over,
 * so the side under the pointer dips down (max ~0.6deg).
 */
const MAX_TILT = 0.6;
function handlePointer(e: PointerEvent) {
  const target = e.target as Element | null;
  const btn = target?.closest?.(".btn-comic") as HTMLElement | null;
  if (!btn) return;
  const rect = btn.getBoundingClientRect();
  const x = (e.clientX - rect.left) / rect.width; // 0 = left, 1 = right
  // Right side under cursor → negative rotation (right edge dips).
  // Left side under cursor → positive rotation (left edge dips).
  const tilt = (x - 0.5) * 2 * MAX_TILT;
  btn.style.setProperty("--btn-comic-tilt", `${tilt.toFixed(3)}deg`);
}
function clearTilt(e: PointerEvent) {
  const target = e.target as Element | null;
  const btn = target?.closest?.(".btn-comic") as HTMLElement | null;
  if (!btn) return;
  btn.style.setProperty("--btn-comic-tilt", "0deg");
}
window.addEventListener("pointermove", handlePointer, { passive: true });
window.addEventListener("pointerdown", handlePointer, { passive: true });
window.addEventListener("pointerleave", clearTilt, { capture: true, passive: true });
window.addEventListener("pointerout", clearTilt, { capture: true, passive: true });

createRoot(document.getElementById("root")!).render(<App />);
